import {
  Component,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ElementRef,
} from "@angular/core";
import {
  MatRowDef,
  MatTableDataSource,
  MatTableModule,
} from "@angular/material/table";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from "@angular/material/paginator";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSidenav, MatSidenavContainer } from "@angular/material/sidenav";
import { MatMenuModule } from "@angular/material/menu";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { DocumentDetailTypeAttributes } from "@albatrosdeveloper/ave-models-package";
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import { Subscription } from "rxjs";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import { StatusFiltersComponent } from '../../filters/status-filters/status-filters.component';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { DocumentLineTypeService } from "./document-type-line.service";
import { ColDef, ColumnFilterOptions, GridConfig, GridReadyEvent, SmartGridComponent, SortChangedEvent } from "app/shared/components/smart-grid";
import { StatusCellRendererComponent } from "./cell-renders/status-cell-render.component";
import { IPaginationResult } from "app/common/interfaces";
import { MatSnackBar } from "@angular/material/snack-bar";
import { NoFiltersAvailableComponent } from "app/common/components/no-filters-available/no-filters-available.component";
import { FilterComponent } from "app/common/components/filter/filter.component";

@Component({
  selector: "app-document-type-line",
  templateUrl: "./document-type-line.component.html",
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    MatTooltipModule,
    MatMenuModule,
    MatIconModule,
    MatRowDef,
    MatSidenav,
    MatSidenavContainer,
    StatusFiltersComponent,
    HoverMenuDirective,
    SmartGridComponent,
    FilterComponent,
    NoFiltersAvailableComponent
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
  ],
})
export class DocumentTypeLineComponent implements AfterViewInit, OnDestroy {
  StatusEnum = StatusEnum
  displayedColumns: string[] = ["code", "name", "isBillable", "isDeliverable", "isBonus", "status"];

  dataSource = new MatTableDataSource<DocumentDetailTypeAttributes>([]);

  searchQuery: string = "";
  totalItems: number = 0;
  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: keyof DocumentDetailTypeAttributes = "name";
  sortDirection: "ASC" | "DESC" = "DESC";

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("searchInput") searchInput!: ElementRef;

  //#region Smart Grid
  // Using `any` because doesn't support DocumentDetailTypeAttributes.
  columnDefs: ColDef<any>[] = [ //DocumentDetailTypeAttributes
    {
      field: 'code',
      headerName: 'Código',
      autoSize: true,
      minWidth: 150,
      sortable: true,
    },
    {
      field: 'name',
      headerName: 'Nombre',
      autoSize: true,
      minWidth: 220,
      sortable: true,
    },
    {
      field: 'isBillable',
      headerName: 'Facturable',
      cellRenderer: StatusCellRendererComponent,
      autoSize: true,
      minWidth: 200,
      sortable: true,
      filterOptions: this.booleanFilterOptions('isBillable')
    },
    {
      field: 'isDeliverable',
      headerName: 'Entregable',
      cellRenderer: StatusCellRendererComponent,
      autoSize: true,
      minWidth: 200,
      sortable: true,
      filterOptions: this.booleanFilterOptions('isDeliverable')
    },
    {
      field: 'isBonus',
      headerName: 'Bonificación',
      cellRenderer: StatusCellRendererComponent,
      autoSize: true,
      minWidth: 200,
      sortable: true,
      filterOptions: this.booleanFilterOptions('isBonus')
    },
    {
      field: 'statusId',
      headerName: 'Estado',
      cellRenderer: StatusCellRendererComponent,
      autoSize: true,
      minWidth: 200,
      sortable: true,
      filterOptions: this.booleanFilterOptions('statusId', StatusEnum.ACTIVE,'Activo','Inactivo')
    },
  ]

  gridConfig: GridConfig<any> = { //DocumentDetailTypeAttributes
    emptyState: {
      message: 'No encontramos tus resultados!',
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: 'api',
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: 'api',
      multiColumn: false,
      defaultSort: [{ field: this.sortActive , direction: this.sortDirection == 'ASC' ? 'asc' : 'desc' }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: 'local'
    },
    columnVisibility: {
      enabled: false,
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40,
    },
    styling: {
      maxHeight: '85vh',
      density: 'comfortable',
      fontSize: '12px',
      cellPaddingX: '12px',
      cellPaddingY: '12px',
    },
    settingsPanel: {
      enabled: true,
      position: 'right',
      enableColumnVisibility: true,
      enableDensity: false,
      enableExport: true,
      defaultDensity: 'comfortable',
    },
  };
  //#endregion

  //BUSCADOR
  searchSubscription: Subscription;

  // Status Filter Properties
  selectedStatuses: number[] = [StatusEnum.ACTIVE, StatusEnum.INACTIVE]; // Default to show both active and inactive

  constructor(
    private readonly documentLineTypeService: DocumentLineTypeService,
    private readonly searchService: SearchService,
    private snackBar: MatSnackBar
  ) { }

  ngAfterViewInit(): void {
    this.onHandleSearch();
    this.findAllWithCount();
  }
 
  findAllWithCount(): void {
    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.searchQuery,
      sort: this.sortActive,
      sortDirection: this.sortDirection,
    };

    this.documentLineTypeService
      .findAllWithCount(options,
        { status: this.selectedStatuses.length ? this.selectedStatuses : null },
      )
      .subscribe({
        next: (res: IPaginationResult<DocumentDetailTypeAttributes>) => {
          this.dataSource.data = res?.data?.[0]?.data ?? [];
          this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al obtener tipos de línea",
            "Cerrar",
            { duration: 5000 }
          );
        },
      })
  }

  changeSort(event: SortChangedEvent<any>){ //DocumentDetailTypeAttributes
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as any;
      this.sortDirection = sortState.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.sortActive = 'name';
      this.sortDirection = 'DESC';
    }
    this.findAllWithCount();
  }

  changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex  + 1;
    this.pageSize = event.pageSize;
    this.findAllWithCount();
  }

  onGridReady(event: GridReadyEvent<DocumentDetailTypeAttributes>): void {
    setTimeout(() => {
      event.api.autoSizeAllColumns();
    }, 100);
  }

  booleanFilterOptions(
    field: keyof DocumentDetailTypeAttributes,
    activeValue: any = true,
    trueLabel = 'Sí',
    falseLabel = 'No'
  ): ColumnFilterOptions<DocumentDetailTypeAttributes> {
    return {
      valueExtractor: (item) => {
        const val = item[field];
        return typeof val === 'boolean' ? val : val === activeValue;
      },
      displayNameGetter: (value: boolean) => value ? trueLabel : falseLabel,
      customFilterFn: (rowData, filterValue) => {
        const val = rowData[field];
        const boolVal = typeof val === 'boolean' ? val : val === activeValue;
        if (filterValue == null) return true;
        return boolVal === !!filterValue;
      },
    };
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$
      .subscribe(term => {
        this.searchQuery = term;
        if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
          this.pageIndex = 1;
          this.findAllWithCount();
        }
      });
  }

  // Status Filter Methods
  applyStatusFilter(): void {
    this.pageIndex = 1;
    this.findAllWithCount();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}
